import { useGetPartnerAgencyInfo } from '@/hooks/useDistributors';
import { PartnerAgencyInfo } from '@/api/distributor.api';
import Modal from '@/components/ui/Modal';
import Loader from '@/components/ui/Loader';
import { useTranslation } from '@/i18n';

interface DistributorInfoModalProps {
  distributorId: string;
  closeModal: () => void;
}

const DistributorInfoModal = ({
  distributorId,
  closeModal,
}: DistributorInfoModalProps) => {
  const {
    data: distributorInfo,
    isSuccess,
    isLoading,
  } = useGetPartnerAgencyInfo(distributorId);

  return (
    <Modal
      actionOnClick={() => {}}
      actionTitle=""
      closeModal={closeModal}
      title="Détails du distributeur"
      hasAction={false}
      cancelBtnLabel="Fermer"
    >
      {isLoading ? (
        <Loader />
      ) : (
        isSuccess && (
          <DistributorInfoModalData
            distributorInfo={distributorInfo}
            distributorId={distributorId}
          />
        )
      )}
    </Modal>
  );
};

export default DistributorInfoModal;

interface DistributorInfoModalDataProps {
  distributorInfo: PartnerAgencyInfo;
  distributorId: string;
}

const DistributorInfoModalData = ({
  distributorInfo,
  distributorId,
}: DistributorInfoModalDataProps) => {
  const { t } = useTranslation();

  const getLabel = (index: number) =>
    index === 0
      ? t('distributors.modals.distributorInfo.director')
      : `${t('distributors.modals.distributorInfo.user')} ${index}`;

  return (
    <>
      <DisplayField
        name={t('distributors.columns.name')}
        data={distributorInfo.name}
      />
      <div className="grid grid-cols-2 gap-4">
        <DisplayField
          name={t('distributors.columns.address')}
          data={distributorInfo.address}
        />
        <DisplayField
          name={t('distributors.columns.city')}
          data={distributorInfo.city}
        />
        <DisplayField
          name={t('distributors.columns.postalCode')}
          data={distributorInfo.postalCode}
        />
        <DisplayField
          name={t('distributors.columns.country')}
          data={distributorInfo.country}
        />
        <DisplayField
          name={t('distributors.columns.website')}
          data={distributorInfo.website}
        />
        <DisplayField
          name={t('distributors.columns.siren')}
          data={distributorInfo.siren}
        />
        <DisplayField
          name={t('distributors.columns.network')}
          data={distributorInfo.network?.name ?? '—'}
        />
      </div>
      {distributorInfo.User.map((user, indexUser) => (
        <div
          className="grid grid-cols-2 gap-4"
          key={`distributor-${distributorId}-user-${indexUser}`}
        >
          <DisplayField
            data={user?.firstname}
            name={`${t('distributors.columns.firstName')} ${getLabel(indexUser)}`}
          />
          <DisplayField
            data={user?.lastname}
            name={`${t('distributors.columns.lastName')} ${getLabel(indexUser)}`}
          />
          <DisplayField
            data={user?.email}
            name={`${t('distributors.columns.email')} ${getLabel(indexUser)}`}
          />
          <DisplayField
            data={user?.phoneNumber}
            name={`${t('distributors.columns.phoneNumber')} ${getLabel(indexUser)}`}
          />
        </div>
      ))}
    </>
  );
};

const DisplayField = ({
  name,
  data,
}: {
  name: string;
  data: string | null;
}) => {
  return (
    data && (
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-800">{name}</span>
        <span className="text-sm text-gray-800">{data}</span>
      </div>
    )
  );
};
